<template>
  <div class="approver-picker">
    <slot name="label" />

    <div class="approver-picker__row">
      <multi-select
        v-if="pickBy === 'people'"
        :id="id"
        :value="value"
        :options="employeeOptions"
        :placeholder="placeholder"
        :searchable="true"
        search-placeholder="Search by name or email"
        :disabled="disabled"
        :select-all="false"
        :select-all-text="selectAllText"
        :max-tags="12"
        class="approver-picker__select mb-0"
        @input="$emit('input', $event)"
      />
      <multi-select
        v-else
        :id="`${id}-divisions`"
        :value="pickedDivisionIds"
        :options="divisionOptions"
        placeholder="Select divisions"
        :disabled="disabled"
        :select-all="false"
        class="approver-picker__select mb-0"
        @input="onDivisionsChange"
      />
      <app-input
        v-if="divisions.length"
        v-model="pickByChoice"
        type="radio"
        name="Add approvers by"
        :options="PICK_BY_OPTIONS"
        :validate-immediate="false"
        button-variant="outline-primary"
        buttons
        class="approver-picker__toggle mb-0"
      />
    </div>

    <p v-if="pickBy === 'division'" class="approver-picker__hint">
      Everyone in these divisions becomes an approver now. People who join later aren't added.
    </p>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import AppInput from '@/components/AppInput.vue';
import MultiSelect from '@/components/MultiSelect.vue';

const PICK_BY_OPTIONS = [
  { text: 'Individuals', value: 'people' },
  { text: 'Divisions', value: 'division' },
];

// Approvers can be anyone in the company (HERO-2974), so nobody is marked as outside Who can
// receive; picking a division adds its current members.
export default {
  name: 'ApproverPicker',
  components: { AppInput, MultiSelect },
  props: {
    id: { type: String, required: true },
    value: { type: Array, default: () => [] },
    employeeOptions: { type: Array, required: true }, // [{ text, value }]
    divisions: { type: Array, default: () => [] }, // [{ value, label }]
    divisionPairs: { type: Array, default: () => [] }, // [{ employee_id, division_id }]
    placeholder: { type: String, default: 'Select who can approve this award' },
    selectAllText: { type: String, default: 'No approvers available' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const pickByChoice = ref('people');
    // Without divisions to pick from (e.g. backup approvers) it's always individuals.
    const pickBy = computed(() => (props.divisions.length ? pickByChoice.value : 'people'));

    const membersOf = computed(() => {
      const map = new Map();
      for (const { employee_id, division_id } of props.divisionPairs) {
        const key = String(division_id);
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(employee_id);
      }
      return map;
    });

    const divisionOptions = computed(() => props.divisions.map(d => ({ text: d.label, value: String(d.value) })));

    const pickedDivisionIds = computed(() => {
      const chosen = new Set(props.value);
      return divisionOptions.value
        .map(d => d.value)
        .filter(id => {
          const members = membersOf.value.get(id) || [];
          return members.length > 0 && members.every(m => chosen.has(m));
        });
    });

    const onDivisionsChange = ids => {
      const next = new Set(ids);
      const added = ids.filter(id => !pickedDivisionIds.value.includes(id));
      const removed = pickedDivisionIds.value.filter(id => !next.has(id));
      const chosen = new Set(props.value);
      removed.forEach(id => (membersOf.value.get(id) || []).forEach(m => chosen.delete(m)));
      added.forEach(id => (membersOf.value.get(id) || []).forEach(m => chosen.add(m)));
      emit('input', [...chosen]);
    };

    return { PICK_BY_OPTIONS, pickBy, pickByChoice, divisionOptions, pickedDivisionIds, onDivisionsChange };
  },
};
</script>

<style scoped lang="scss">
.approver-picker {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.approver-picker__row {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
}

// The list takes the row; the toggle keeps its width on the right.
.approver-picker__toggle {
  flex-shrink: 0;
}

.approver-picker__select {
  flex: 1;
  min-width: 0;
}

.approver-picker__hint {
  color: var(--palette-color-default-lighten-20);
  font-size: 0.8125rem;
  margin: 0;
}
</style>
